import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { LinkItUrl } from "react-linkify-it";

import {
  Avatar,
  Dropdown,
  PencilIcon,
  CheckIcon,
  CrossIcon,
  MenuDotsIcon,
  TrashIcon,
  ReplayIcon,
} from "@mwi/ui";

import {
  MessagesMessage,
  deleteMessage,
  editMessage,
  restoreMessage,
  setError,
} from "slices/messages";

import DateTimeItem from "../../components/DateTimeItem";
import { useEffect, useRef, useState } from "react";
import { MessagesEvent } from "middlewares/messages";

type MessageCardProps = {
  message: MessagesMessage;
  conversationId: string;
  sentByMe?: boolean;
  "data-test-id"?: string;
};

const MessageCard = ({
  message,
  conversationId,
  sentByMe,
  "data-test-id": dataTestId,
}: MessageCardProps): JSX.Element => {
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();
  if (isEditing && sentByMe) {
    return (
      <MessageEditor
        conversationId={conversationId}
        message={message}
        dataTestId={dataTestId}
        setIsEditing={setIsEditing}
      />
    );
  }
  if (message.state === "DELETED") {
    return (
      <div
        data-test-id={`MessageCard_${message.id}`}
        className={`mb-6 ${sentByMe ? "tablet:pl-20" : "tablet:pr-20"}`}
      >
        <div className="flex items-center justify-center">
          <span className="flex-grow h-[1px] bg-gray-300 mx-2"></span>
          <span className="text-sm text-gray-500 font-medium select-none text-center px-2">
            Deleted
          </span>
          <span className="flex-grow h-[1px] bg-gray-300 mx-2"></span>
          {sentByMe ? (
            <Dropdown
              trigger={
                <span className="p-1 ml-1 mt-2 inline-block rounded-md hover:bg-gray-100 border-transparent hover:border-gray-200 border-solid border">
                  <MenuDotsIcon className="w-[18px] h-[18px]" />
                </span>
              }
            >
              <Dropdown.Item
                onClick={() => {
                  dispatch(
                    restoreMessage({ conversationId, messageId: message.id }),
                  );
                }}
              >
                <>
                  <ReplayIcon className="w-[18px] h-[18px] mr-1 tablet:mr-2" />
                  Restore message
                </>
              </Dropdown.Item>
            </Dropdown>
          ) : null}
        </div>
      </div>
    );
  }
  function handleDelete() {
    dispatch(deleteMessage({ conversationId, messageId: message.id }));
  }
  return (
    <div
      data-test-id={dataTestId}
      className={classNames("MessageCard mb-6", {
        "tablet:pl-20": sentByMe,
        "tablet:pr-20": !sentByMe,
      })}
    >
      <div
        className={classNames(
          "p-4 pb-6 bg-white shadow-default tablet:shadow-none tablet:border-grey-light tablet:border-l-2",
          { "order-last": sentByMe },
        )}
      >
        <div className="flex flex-row justify-between items-center mb-2">
          <div className="flex flex-row gap-3 items-center">
            <Avatar
              data-test-id={`${dataTestId}_Avatar`}
              name={message.sender.displayName}
              className="shrink-0 w-[40px] h-[40px]"
            />
            <p
              className="text-base font-medium overflow-hidden text-ellipsis whitespace-nowrap"
              data-test-id={`${dataTestId}_Name`}
            >
              {sentByMe ? "You" : message.sender.displayName}
            </p>
          </div>
          <div className="flex items-center">
            <DateTimeItem
              time={message.time}
              creation={
                message.state === "EDITED" ? message.created_time : undefined
              }
              data-test-id={`${dataTestId}_Date`}
            />
            {sentByMe ? (
              <Dropdown
                trigger={
                  <span className="p-1 ml-1 inline-block rounded-md hover:bg-gray-100 border-transparent hover:border-gray-200 border-solid border">
                    <MenuDotsIcon className="w-[18px] h-[18px]" />
                  </span>
                }
              >
                <Dropdown.Item onClick={() => setIsEditing(!isEditing)}>
                  <>
                    <PencilIcon className="w-[18px] h-[18px] mr-2" /> Edit
                    message
                  </>
                </Dropdown.Item>
                <Dropdown.Item onClick={handleDelete}>
                  <>
                    <TrashIcon className="w-[18px] h-[18px] mr-2" /> Delete
                    message
                  </>
                </Dropdown.Item>
              </Dropdown>
            ) : null}
          </div>
        </div>

        <div className="MessageCard_Body flex flex-row gap-3 items-center">
          <div className="MessageCard_AvatarSpacer shrink-0 w-[40px] h-[1px]"></div>
          <p className="[&>a]:underline" data-test-id={`${dataTestId}_Message`}>
            <LinkItUrl>{message.message}</LinkItUrl>
          </p>
        </div>
      </div>
    </div>
  );
};

const MessageEditor = ({
  message,
  conversationId,
  dataTestId,
  setIsEditing,
}: {
  message: MessagesMessage;
  conversationId: string;
  dataTestId?: string;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [messageText, setMessageText] = useState(message.message);
  const ref = useRef<HTMLTextAreaElement>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
      ref.current.selectionStart = ref.current.selectionEnd = 10000;
    }
  }, [ref]);

  const saveEdit = () => {
    if (messageText.trim().length) {
      if (messageText !== message.message) {
        dispatch(
          editMessage({
            conversationId,
            messageId: message.id,
            message: messageText,
          }),
        );
      }
      setIsEditing(false);
    } else {
      dispatch(
        setError({
          source: MessagesEvent.EditMessage,
          message: "Edited text cannot be empty",
        }),
      );
    }
  };

  const handleEscapeKey = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsEditing(false);
    }
  };

  return (
    <div data-test-id={dataTestId} className="MessageCard mb-6 tablet:pl-20">
      <div className="p-4 pb-6 bg-white shadow-default tablet:shadow-none tablet:border-grey-light tablet:border-l-2 order-last">
        <div className="flex flex-row justify-between items-center mb-2">
          <div className="flex flex-row gap-3 items-center">
            <Avatar
              data-test-id={`${dataTestId}_Avatar`}
              name={message.sender.displayName}
              className="shrink-0 w-[40px] h-[40px]"
            />
            <p
              className="text-base font-medium overflow-hidden text-ellipsis whitespace-nowrap"
              data-test-id={`${dataTestId}_Name`}
            >
              You
            </p>
          </div>
          <div className="flex items-center">
            <button
              className="p-1 ml-1 border-green-200 border-solid border rounded-md bg-green-50 hover:bg-green-200"
              onClick={saveEdit}
            >
              <CheckIcon className="w-[22px] h-[22px]" />
            </button>
            <button
              className="p-1 ml-1 border-red-200 border-solid border rounded-md bg-red-50 hover:bg-red-200"
              onClick={() => setIsEditing(false)}
            >
              <CrossIcon className="w-[22px] h-[22px]" />
            </button>
          </div>
        </div>

        <div className="MessageCard_Body flex flex-row gap-3 items-center">
          <div className="MessageCard_AvatarSpacer shrink-0 w-[40px] h-[1px]"></div>
          <textarea
            ref={ref}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleEscapeKey}
            className="grow p-1 border border-blue-300 rounded-md focus:border-blue-700"
          ></textarea>
        </div>
      </div>
    </div>
  );
};

export default MessageCard;
